import * as Sentry from "@sentry/react";
import { isDeviceOnline } from "./services/network";
import { resolveKnownAppType } from "./utils/webAppType";
import { PRODUCTS } from "./product";

/**
 * One Sentry project per app, so GreekBrain's errors and session replays never
 * land in Hallouminati's issue stream (and vice versa). The DSNs live in
 * product/index.ts next to the other per-product keys.
 */

/**
 * Normalize a request URL for grouping: drop query string and replace numeric
 * path IDs so e.g. `/api/sessions/123/turn?x=1` → `/api/sessions/:id/turn`.
 */
function normalizeEndpoint(url) {
    if (!url || typeof url !== 'string') return 'unknown';
    return url.replace(/\?.*$/, '').replace(/\/\d+/g, '/:id');
}

/**
 * Central reporting policy for connectivity-related errors. Runs for EVERY
 * Sentry event (manual captures, the global query/mutation handlers, unhandled
 * rejections and the React error boundary), so all capture sites get the same
 * treatment without each one needing its own filtering.
 *
 * Returns the (possibly rewritten) event, or `null` to drop it.
 */
function applyServerConnectivityPolicy(event, hint) {
    const ex = hint && hint.originalException;
    const isApiError = !!ex && typeof ex === 'object' && ex.name === 'ApiError';

    if (isApiError) {
        const statusCode = typeof ex.statusCode === 'number' ? ex.statusCode : undefined;
        const isServerError = statusCode !== undefined && statusCode >= 500;
        const isNetworkError = !!ex.isNetworkError;

        // 1) Device has no internet → expected, never report. Use the snapshot
        //    captured when the error was thrown; fall back to live status.
        if (ex.isOffline || (isNetworkError && !isDeviceOnline())) {
            return null;
        }

        // 2) Server-connectivity problems: a 5xx response, OR a network failure
        //    while the device IS online (server unreachable / timed out / DNS).
        //    Collapse them ALL into a single Sentry issue, with the specifics
        //    (status, endpoint, url, response body) attached as searchable
        //    tags/extra. Dedupe re-captures of the same error object so the
        //    feature-level onError handlers don't each add a duplicate event.
        if (isServerError || isNetworkError) {
            if (ex.__scReported) return null;
            ex.__scReported = true;

            const endpoint = normalizeEndpoint(ex.url);
            event.fingerprint = ['server-connection-error'];
            event.level = 'error';
            event.tags = {
                ...event.tags,
                error_type: 'server_connection',
                reason: isServerError ? `http_${statusCode}` : 'unreachable',
                status_code: statusCode !== undefined ? String(statusCode) : 'network',
                api_error_code: ex.errorCode || 'none',
                endpoint,
                http_method: ex.method || 'unknown',
            };
            event.extra = {
                ...event.extra,
                url: ex.url,
                method: ex.method,
                statusCode,
                errorCode: ex.errorCode,
                responseData: ex.responseData,
                userMessage: ex.message,
            };
            if (event.exception && event.exception.values && event.exception.values[0]) {
                event.exception.values[0].type = 'ServerConnectionError';
                event.exception.values[0].value = isServerError
                    ? `Server error (${statusCode}) at ${endpoint}`
                    : `Unable to reach server at ${endpoint}`;
            }
        }

        return event;
    }

    // Non-ApiError thrown while the device is offline and it looks like a
    // connectivity failure (fetch/XHR/WebSocket) → drop the offline noise.
    if (ex && !isDeviceOnline()) {
        const msg = (ex.message || String(ex) || '');
        if (/network|internet|failed to fetch|load failed|connection|offline|ERR_/i.test(msg)) {
            return null;
        }
    }

    return event;
}

let initialized = false;

/**
 * Start Sentry against the project that owns `appType`.
 *
 * Called at module load for every web/Telegram load and every native launch
 * after the first — i.e. whenever the app identity is known synchronously. A
 * fresh native install has to await the bundle id, so PlatformProvider calls
 * this again once `platformService.getAppType()` resolves. First call wins.
 */
export function initSentry(appType) {
    if (initialized) return;

    // Only initialize Sentry in production environment
    if (import.meta.env.VITE_ENVIRONMENT !== 'production') return;

    initialized = true;

    Sentry.init({
        dsn: (PRODUCTS[appType] ?? PRODUCTS.hallouminati).sentryDsn,
        // Adds request headers and IP for users, for more info visit:
        // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
        sendDefaultPii: true,
        initialScope: {
            tags: { app_type: appType },
        },
        integrations: [
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
        ],
        // Session Replay
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
        beforeSend: applyServerConnectivityPolicy,
    });
}

const knownAppType = resolveKnownAppType();
if (knownAppType) {
    initSentry(knownAppType);
}
